import {
  formatDuration,
  holdsPlace,
  handoverMoments,
  handoverProgress,
  liveShiftViews,
  liveStatusIn,
  type LiveShiftView,
  type LiveStatus,
  LATE_WARN_STEPS,
  LIVE_HANDOVER_COUNT,
  minutesToHm,
  seenAcrossMidnight,
  STARTING_SOON_MINUTES,
} from '@reditelji/shared';
import type { CheckinRow, Shift, TimelinePayload } from '../api/types';

/** Latest check-in/out rows per shift (last write wins). */
export interface ShiftCheckins {
  in?: CheckinRow;
  out?: CheckinRow;
}

export function checkinsByShift(rows: CheckinRow[]): Map<string, ShiftCheckins> {
  const map = new Map<string, ShiftCheckins>();
  for (const row of rows) {
    const entry = map.get(row.shiftId) ?? {};
    entry[row.kind] = row; // rows arrive ordered by createdAt — last wins
    map.set(row.shiftId, entry);
  }
  return map;
}

/**
 * What `liveShiftViews` reads of a shift: its own check-in and check-out, and
 * for the 00:00 half of a shift split at midnight the arrival at its 24:00
 * half (`seenAcrossMidnight`).
 */
export const seenIn = (cm: Map<string, ShiftCheckins>) => (s: Shift) =>
  seenAcrossMidnight(s, { inAt: cm.get(s.id)?.in?.atMin ?? null, outAt: cm.get(s.id)?.out?.atMin ?? null });

/**
 * How every shift reads within its person's stretch of back-to-back shifts at
 * its post (`liveShiftViews`), by shift id — what Časovnica and Predaje ask so
 * that they say what the map says.
 */
export function liveViewsByShift(
  posts: readonly { shifts: Shift[] }[],
  cm: Map<string, ShiftCheckins>,
  now: number,
): Map<string, LiveShiftView> {
  const views = new Map<string, LiveShiftView>();
  for (const p of posts) for (const [id, view] of liveShiftViews(p.shifts, seenIn(cm), now)) views.set(id, view);
  return views;
}

/**
 * On-site state of one shift somebody holds (`holdsPlace`), from check-in rows
 * + shift state. With its `view`, a shift carried on into counts the check-in
 * to the one before.
 */
export type CiState = 'prijavljen' | 'opravljeno' | null;

export function ciState(shift: Shift, cm: Map<string, ShiftCheckins>, view?: LiveShiftView): CiState {
  if (!holdsPlace(shift)) return null;
  // The view carries the shift's own rows, and the arrival of the shift before.
  const seen = view ?? seenIn(cm)(shift);
  if (shift.state === 'done' || seen.outAt !== null) return 'opravljeno';
  if (seen.inAt !== null) return 'prijavljen';
  return null;
}

/**
 * Which check-in buttons a shift offers at `now` — the only spelling of it, for
 * the map, Predaje and Časovnica alike. An arrival is recorded once per
 * stretch: a shift carried on into offers none, and a shift carried on offers
 * nothing, since checking it out would end the stretch. Somebody who never
 * checked in can still be seen off once their shift has begun.
 */
export function checkinActions(
  shift: Shift,
  view: LiveShiftView,
  now: number,
  external: boolean,
): { in: boolean; out: boolean } {
  if (!holdsPlace(shift) || external || shift.state === 'done' || view.outAt !== null || view.continued) {
    return { in: false, out: false };
  }
  const arrived = view.inAt !== null;
  return { in: !arrived, out: arrived || now >= shift.fromMin };
}

/**
 * What `handoverProgress` reads: who arrived, who left, and who is a team. With
 * the stretch `views`, an arrival at the shift before counts for the one
 * carried on into.
 */
export function seenFrom(cm: Map<string, ShiftCheckins>, external: Set<string>, views?: Map<string, LiveShiftView>) {
  const seenOf = (s: Shift) => views?.get(s.id) ?? seenIn(cm)(s);
  return {
    // Seen off means they were there, even with no arrival recorded.
    arrived: (s: Shift) => s.state === 'done' || seenOf(s).inAt !== null || seenOf(s).outAt !== null,
    left: (s: Shift) => s.state === 'done' || seenOf(s).outAt !== null,
    external: (s: Shift) => s.volunteerId !== null && external.has(s.volunteerId),
  };
}

/** People on site now: checked in, each stretch of back-to-back shifts once. */
export function onSiteCount(shifts: Shift[], cm: Map<string, ShiftCheckins>, views?: Map<string, LiveShiftView>): number {
  return shifts.filter((s) => {
    const view = views?.get(s.id);
    return ciState(s, cm, view) === 'prijavljen' && !view?.continued;
  }).length;
}

/**
 * Late level: -1 not applicable · 0 should be there, no check-in ·
 * 1 = 15+ min late · 2 = 30+ min late. Only meaningful in live mode for a
 * shift somebody holds (`holdsPlace`), un-checked-in, whose window contains
 * `now`. With its `view`, lateness counts from the start of the stretch.
 */
export function lateLevel(
  shift: Shift,
  cm: Map<string, ShiftCheckins>,
  now: number,
  live: boolean,
  isExternal = false,
  view?: LiveShiftView,
): -1 | 0 | 1 | 2 {
  if (!live || !holdsPlace(shift)) return -1;
  // An external team checks in with its own leader, not with us (SPEC §1.1J).
  // Without this every one of their shifts would escalate through the whole
  // event day, with nothing a coordinator could do to clear it.
  if (isExternal) return -1;
  if (view?.continued || ciState(shift, cm, view) !== null) return -1;
  if (shift.fromMin > now || shift.toMin <= now) return -1;
  const m = now - (view?.lateFrom ?? shift.fromMin);
  return m >= LATE_WARN_STEPS[1] ? 2 : m >= LATE_WARN_STEPS[0] ? 1 : 0;
}

/** Escalation ladder palette. */
export const LATE_LEVELS = [
  { label: 'ni prijave', bg: 'oklch(0.975 0.035 60)', bd: 'oklch(0.86 0.08 60)', fg: 'oklch(0.45 0.11 55)' },
  { label: 'zamuja 15+ min', bg: 'oklch(0.97 0.035 25)', bd: 'oklch(0.84 0.09 25)', fg: 'oklch(0.46 0.14 25)' },
  { label: 'zamuja 30+ min', bg: 'oklch(0.955 0.05 25)', bd: 'oklch(0.76 0.13 25)', fg: 'oklch(0.42 0.15 25)' },
] as const;

/** Late-block palette on the timeline. */
/** The same steps as the map (SPEC §1.2): orange before 15 minutes, red from then. */
export const LATE_BLOCK = [
  { bg: 'oklch(0.97 0.04 60)', bd: 'oklch(0.72 0.14 60)', fg: 'oklch(0.42 0.11 55)', dot: 'var(--rr-live-ni)' },
  { bg: 'oklch(0.96 0.045 25)', bd: 'oklch(0.62 0.16 27)', fg: 'oklch(0.42 0.14 25)', dot: 'var(--rr-live-zamuja)' },
  { bg: 'oklch(0.95 0.055 25)', bd: 'oklch(0.56 0.18 27)', fg: 'oklch(0.4 0.15 25)', dot: 'var(--rr-live-zamuja)' },
] as const;

export interface LiveLateRow {
  shift: Shift;
  name: string;
  where: string;
  since: string;
  level: 0 | 1 | 2;
}

export interface LiveHandRow {
  key: string;
  at: number;
  postId: string;
  postCode: string;
  micro: string;
  outs: Shift[];
  ins: Shift[];
  outName: string;
  inName: string;
  /** Everyone arriving has checked in. */
  inOnSite: boolean;
  /** How many of them have. */
  arrived: number;
  expected: number;
  /** Every incoming shift is an external team's (SPEC §1.1J). */
  inExternal: boolean;
  /** Its leader, when one is on file — the number to call. */
  inLeader: { name: string; phone: string } | null;
  risky: boolean;
  done: boolean;
}

export interface LiveSoonRow {
  shift: Shift;
  name: string;
  where: string;
  startsIn: number;
  /** The shift is an external team's (SPEC §1.1J). */
  external: boolean;
  /** Who to phone, when a leader's number is on file. */
  leader: { name: string; phone: string } | null;
  /**
   * Nobody is on it: an open slot starting soon or already running. A slot
   * freed by a "Ne pride" on race morning is exactly this (SPEC §1.1K).
   */
  open: boolean;
}

export interface LiveVals {
  onSite: number;
  lateList: LiveLateRow[];
  handList: LiveHandRow[];
  handCount: number;
  soonList: LiveSoonRow[];
}

/**
 * Live panel data: late list sorted worst-first, next
 * handovers (`handoverMoments`) windowed to now−15 … now+150 and capped at LIVE_HANDOVER_COUNT,
 * shifts starting within STARTING_SOON_MINUTES.
 */
export function liveVals(data: TimelinePayload, now: number, live: boolean): LiveVals {
  const cm = checkinsByShift(data.checkins);
  const volunteersById = new Map(data.volunteers.map((v) => [v.id, v]));
  const postsById = new Map(data.posts.map((p) => [p.id, p]));
  const shifts = data.posts.flatMap((p) => p.shifts);
  const nameOf = (id: string | null) => (id ? (volunteersById.get(id)?.name ?? '?') : '?');
  const isExternalId = (id: string | null) =>
    id !== null && volunteersById.get(id)?.externalGroupId != null;
  const isExternalShift = (s: Shift) => isExternalId(s.volunteerId);
  const groupById = new Map(data.externalGroups.map((g) => [g.id, g]));
  /** Who to phone about this shift: the team's leader, when it is a team's. */
  const leaderOf = (id: string | null) => {
    const gid = id ? volunteersById.get(id)?.externalGroupId : null;
    const g = gid ? groupById.get(gid) : undefined;
    return g && g.leaderPhone ? { name: g.leaderName || g.name, phone: g.leaderPhone } : null;
  };
  const whereOf = (postId: string) => {
    const p = postsById.get(postId);
    return p ? `${p.code} · ${p.micro}` : '?';
  };

  const views = live ? liveViewsByShift(data.posts, cm, now) : undefined;
  const onSite = onSiteCount(shifts, cm, views);

  const lateList: LiveLateRow[] = [];
  for (const s of shifts) {
    const view = views?.get(s.id);
    const lv = lateLevel(s, cm, now, live, isExternalShift(s), view);
    if (lv < 0) continue;
    const from = view?.lateFrom ?? s.fromMin;
    lateList.push({
      shift: s,
      name: nameOf(s.volunteerId),
      where: whereOf(s.postId),
      since: `začetek ${minutesToHm(from)} · ${formatDuration(now - from)} nazaj`,
      level: lv as 0 | 1 | 2,
    });
  }
  lateList.sort((a, b) => b.level - a.level);

  const external = new Set(data.volunteers.filter((v) => v.externalGroupId != null).map((v) => v.id));
  const seen = seenFrom(cm, external, views);
  const names = (list: Shift[]) => list.map((s) => (holdsPlace(s) ? nameOf(s.volunteerId) : 'PROSTO')).join(' + ');
  const all = handoverMoments(shifts).filter(
    (m) => m.kind === 'predaja' && m.atMin >= now - 15 && m.atMin - now <= 150,
  );
  const handList: LiveHandRow[] = all.slice(0, LIVE_HANDOVER_COUNT).map((m) => {
    const p = postsById.get(m.postId);
    const progress = handoverProgress(m, seen, now);
    const inExternal = m.ins.every(isExternalShift);
    const leaderShift = m.ins.find(isExternalShift);
    return {
      key: m.id,
      at: m.atMin,
      postId: m.postId,
      postCode: p?.code ?? '?',
      micro: p?.micro ?? '',
      outs: m.outs,
      ins: m.ins,
      outName: names(m.outs),
      inName: names(m.ins),
      inOnSite: progress.complete,
      arrived: progress.done,
      expected: progress.expected,
      inExternal,
      inLeader: leaderShift ? leaderOf(leaderShift.volunteerId) : null,
      // A team's arrival is not something we can check in advance, so it never
      // reads as "risky" for want of a check-in. Keyed off the team itself, not
      // off having a leader's phone: a team entered without one is still a team.
      risky: !progress.complete && !inExternal && m.atMin - now <= 20,
      done: progress.complete,
    };
  });

  const isOpen = (s: Shift) => s.state === 'open' && !s.volunteerId;
  const soonList: LiveSoonRow[] = shifts
    .filter((s) =>
      isOpen(s)
        ? // An empty slot matters from the moment it is near until it is over —
          // running with nobody there is the most urgent case of all.
          s.toMin > now && s.fromMin - now <= STARTING_SOON_MINUTES
        : holdsPlace(s) &&
          s.fromMin > now &&
          s.fromMin - now <= STARTING_SOON_MINUTES,
    )
    .sort((a, b) => a.fromMin - b.fromMin)
    .map((s) => ({
      shift: s,
      name: isOpen(s) ? 'PROSTO' : nameOf(s.volunteerId),
      where: whereOf(s.postId),
      startsIn: s.fromMin - now,
      external: isExternalShift(s),
      leader: leaderOf(s.volunteerId),
      open: isOpen(s),
    }));

  return { onSite, lateList, handList, handCount: all.length, soonList };
}

/** Live-mode legend rows, per mode. */
export const LIVE_KEY = [
  { label: 'na mestu', bg: 'oklch(0.95 0.045 155)', bd: '1px solid var(--rr-live-na)' },
  { label: 'ni prijave', bg: 'oklch(0.97 0.04 60)', bd: '1px solid var(--rr-live-ni)' },
  { label: 'zamuja', bg: 'oklch(0.96 0.045 25)', bd: '1px solid var(--rr-live-zamuja)' },
  { label: 'opravljeno', bg: 'oklch(0.96 0.025 300)', bd: '1px solid var(--rr-live-odjavljen)' },
  { label: 'odpoved', bg: 'oklch(0.97 0.02 25)', bd: '1px solid oklch(0.85 0.05 25)' },
  { label: 'nezasedeno', bg: 'oklch(0.965 0.004 95)', bd: '1px dashed oklch(0.86 0.008 95)' },
] as const;

/** Where one shift stands right now, in the words a card shows (SPEC §1.1I). */
export interface LiveChip {
  status: LiveStatus;
  text: string;
}

export function liveChip(
  s: Shift,
  cm: Map<string, ShiftCheckins>,
  now: number,
  external: boolean,
  /** How the shift reads within the person's stretch of back-to-back shifts, where one is known. */
  view?: LiveShiftView,
): LiveChip | null {
  const seen = view ?? { ...seenIn(cm)(s), lateFrom: s.fromMin, continued: false };
  const status = liveStatusIn(s, seen, now, { external });
  if (status === null) return null;
  if (seen.continued && status === 'na') return { status, text: 'nadaljuje' };
  const late = now - (now >= s.fromMin ? seen.lateFrom : s.fromMin);
  const text: Record<LiveStatus, string> = {
    kasneje: `izmena ob ${minutesToHm(s.fromMin)}`,
    kmalu: `izmena čez ${formatDuration(s.fromMin - now)}`,
    na: seen.inAt !== null ? `na mestu od ${minutesToHm(seen.inAt)}` : 'na mestu',
    ni: `ni prijave · ${formatDuration(late)}`,
    zamuja: `zamuja ${formatDuration(late)}`,
    brez: 'ni prišel',
    odjavljen: seen.outAt !== null ? `odjavljen ${minutesToHm(seen.outAt)}` : 'opravljeno',
    niodj: 'ni odjave',
    prosto: 'PROSTO',
    ekipa: 'zunanja ekipa',
  };
  return { status, text: text[status] };
}
